/** * プレイス編集コンポーネント */
<template>
  <div class="place-editor">
    <ifield-b-modal
      v-bind:id="modalId"
      ref="place-editor-dialog-modal"
      v-bind:title="messages.modalTitle"
      centered
      scrollable
      no-body-border
      size="lg"
      ok-variant="success"
      ignore-enforce-focus-selector=".popover"
      v-on:shown="$_onShownModal"
      v-on:hide="$_onHideModal"
    >
      <loading-mask isFullScreen v-bind:isLoading="isSaving" />
      <form-group
        id="place-form"
        ref="form-group"
        v-bind:mapTypes="$store.state.login.mapTypes"
        v-bind:onIfieldApiError="handleApiError"
      >
        <!-- slot:位置選択地図 -->
        <template
          v-slot:[`${geometryFieldName}-map`]="{
            idPrefix,
            editStart,
            onEditEnd,
          }"
        >
          <map-control
            ref="map-control"
            id="place-editor-map-control"
            v-bind="$store.getters.mapControlBinds"
            v-bind:id-prefix="idPrefix"
            v-bind:height="'80vh'"
            v-bind:can-change-indoor-level="false"
            v-bind:onIfieldApiError="handleApiError"
            v-on:menu-layers-visible-changed="
              $store.dispatch('map/setVisible', $event)
            "
            v-on:map-control-initend="$_onMapInitEnd(editStart)"
            v-on:map-control-layer-editend="onEditEnd"
          />
        </template>
        <!-- slot:現場 -->
        <template v-slot:indoor_site_id="{ value, setValue }">
          <indoor-site-selector
            size="md"
            v-bind:selectedAllLabel="''"
            v-bind:value="value"
            v-on:indoor-site-changed="$_setSiteId($event, setValue)"
          />
        </template>
        <!-- slot:建物 -->
        <template v-slot:indoor_building_id="{ value, setValue }">
          <indoor-building-selector
            size="md"
            v-bind:selectedAllLabel="''"
            v-bind:value="value"
            v-bind:indoorSiteId="siteId"
            v-on:indoor-building-changed="$_setBuildingId($event, setValue)"
            v-on:indoor-building-options-changed="$_setBuildingOptions"
          />
        </template>
        <!-- slot:屋内レベル -->
        <template v-slot:indoor_level_id="{ value, setValue }">
          <indoor-level-selector
            size="md"
            v-bind:selectedAllLabel="''"
            v-bind:value="value"
            v-bind:withBuildingName="!buildingId"
            v-bind:indoorBuildingIds="buildingOptionIds"
            v-on:indoor-level-changed="$_setLevelId($event, setValue)"
          />
        </template>
      </form-group>

      <!--
        OKボタンクリック時に $_onHideModal を実行したくないが
        b-modal の ok イベントは prevent しても hide イベントが発行されてしまうので
        ok イベントが全く関与しないように slot でボタンの並びを再現する
       -->
      <template v-slot:modal-footer="{ cancel }">
        <b-button size="sm" variant="secondary" v-on:click="cancel">
          {{ messages.modalCancel }}
        </b-button>
        <b-button size="sm" variant="success" v-on:click="$_savePlace">
          {{ messages.modalOk }}
        </b-button>
      </template>
    </ifield-b-modal>
    <loading-mask isFullScreen v-bind:isLoading="isLoading" />
  </div>
</template>

<script>
import { ATTRIBUTE_TYPE } from 'ifield-core-components'
import { RestApiClient } from 'ifield-core-components'
import { mapState, mapGetters } from 'vuex'
import IndoorSiteSelector from '@/components/indoor/AppIndoorSiteSelector.vue'
import IndoorBuildingSelector from '@/components/indoor/AppIndoorBuildingSelector.vue'
import IndoorLevelSelector from '@/components/indoor/AppIndoorLevelSelector.vue'
import {
  DATE_FORMAT_HYPHEN,
  TIME_FORMAT_SECOND,
  FUNCTION_TYPE,
  NO_OP,
} from '@/constants.js'
import { toLocalTzAttribute, toServerTz } from '@/js/TimezoneHelper'
import _ from 'lodash'
import moment from 'moment'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'
import { useMapIndoorStore } from '@/composables/map/UseMapIndoorStore'

// フィールド名
const FIELD_NAME = {
  PLACE_NAME: 'place_name',
  PLACE_NAME_KANA: 'place_name_kana',
  PLACE_CODE: 'place_code',
  POSTAL_CODE: 'postal_code',
  ADDRESS1: 'address1',
  ADDRESS2: 'address2',
  TEL: 'tel',
  FAX: 'fax',
  REMARKS: 'remarks',
  INDOOR_SITE: 'indoor_site_id',
  INDOOR_BUILDING: 'indoor_building_id',
  INDOOR_LEVEL: 'indoor_level_id',
  GEOMETRY: 'geometry',
  PLACE_GROUPS: 'place_groups',
  PLACE_USER_RELATIONS: 'place_user_relations',
  PLACE_ATTACH_FILES: 'place_attach_files',
}

// フォーム用日時フォーマット
const DATETIME_FORMAT_FORM = `${DATE_FORMAT_HYPHEN} ${TIME_FORMAT_SECOND}`
// ユーザー属性用複数項目セパレータ
const ATTRIBUTE_VALUES_SEPARATOR = '|'

/**
 * プレイス編集コンポーネント
 */
export default {
  name: 'ThePlaceEditor',
  components: {
    'indoor-site-selector': IndoorSiteSelector,
    'indoor-building-selector': IndoorBuildingSelector,
    'indoor-level-selector': IndoorLevelSelector,
  },
  props: {
    /**
     * DOMのidのプレフィックス
     */
    idPrefix: {
      type: String,
      default: '',
    },
  },
  setup(props, context) {
    const { handleApiError, showApiError } = useHandleApiError(props, context)
    const { changeIndoorBuilding, setActiveIndoorLevelId } = useMapIndoorStore()
    return {
      handleApiError,
      showApiError,
      changeIndoorBuilding,
      setActiveIndoorLevelId,
    }
  },
  data: function () {
    return {
      // プレイス情報
      data: null,
      // プレイスグループ情報
      groups: null,
      // プレイスユーザー属性情報
      attributes: null,
      // 新規作成か？
      isCreate: true,
      // 現場選択値
      siteId: null,
      // 建物選択値
      buildingId: null,
      // 建物の選択肢
      buildingOptions: null,
      // 屋内レベルID
      levelId: null,
      // ロード中か？
      isLoading: false,
      // 保存中か？
      isSaving: false,
      // 表示前の元屋内建物
      originalIndoorBuildings: [],
      // 表示前の元屋内レベルID
      originalActiveIndoorLevelId: null,
    }
  },
  computed: {
    ...mapState('indoorBuilding', ['indoorBuildings']),
    ...mapState('indoorLevel', ['indoorLevels']),
    ...mapState('mapIndoor', ['activeIndoorLevelId', 'activeIndoorBuildingId']),
    ...mapState('mapIndoor', {
      activeIndoorBuildings: (state) => state.indoorBuildings,
    }),
    ...mapGetters('userKeywordTarget', ['keywordTargets']),
    // モーダルのID
    modalId: function () {
      return `${this.idPrefix}-place-editor-dialog`
    },
    /**
     * 屋内機能が利用可能か？
     * @type {Boolean}
     */
    isIndoorAvailable: function () {
      return !!this.$store.getters.functionType(FUNCTION_TYPE.INDOOR)
    },
    /**
     * プレイス位置フィールド名称
     * @type {String}
     */
    geometryFieldName: function () {
      return FIELD_NAME.GEOMETRY
    },
    // 表示文字列
    messages: function () {
      if (this.isCreate) {
        return {
          modalTitle: this.$t('thePlaceEditor.message.createPlace'),
          modalOk: this.$t('thePlaceEditor.action.create'),
          modalCancel: this.$t('thePlaceEditor.action.cancel'),
          successTitle: this.$t('thePlaceEditor.message.created'),
          errorTitle: this.$t('thePlaceEditor.message.failedToCreate'),
        }
      } else {
        return {
          modalTitle: this.$t('thePlaceEditor.message.editPlace'),
          modalOk: this.$t('thePlaceEditor.action.save'),
          modalCancel: this.$t('thePlaceEditor.action.cancel'),
          successTitle: this.$t('thePlaceEditor.message.saved'),
          errorTitle: this.$t('thePlaceEditor.message.failedToSave'),
        }
      }
    },
    /**
     * フィールド名のMap
     * @type {Map}
     */
    fieldLabelMap: function () {
      return new Map([
        [FIELD_NAME.PLACE_NAME, this.$t('thePlaceEditor.field.placeName')],
        [
          FIELD_NAME.PLACE_NAME_KANA,
          this.$t('thePlaceEditor.field.placeNameKana'),
        ],
        [FIELD_NAME.PLACE_CODE, this.$t('thePlaceEditor.field.placeCode')],
        [FIELD_NAME.POSTAL_CODE, this.$t('thePlaceEditor.field.postalCode')],
        [FIELD_NAME.ADDRESS1, this.$t('thePlaceEditor.field.address1')],
        [FIELD_NAME.ADDRESS2, this.$t('thePlaceEditor.field.address2')],
        [FIELD_NAME.TEL, this.$t('thePlaceEditor.field.tel')],
        [FIELD_NAME.FAX, this.$t('thePlaceEditor.field.fax')],
        [FIELD_NAME.REMARKS, this.$t('thePlaceEditor.field.remarks')],
        [FIELD_NAME.INDOOR_LEVEL, this.$t('thePlaceEditor.field.indoorLevel')],
        [FIELD_NAME.GEOMETRY, this.$t('thePlaceEditor.field.geometry')],
        [FIELD_NAME.PLACE_GROUPS, this.$t('thePlaceEditor.field.placeGroups')],
        [
          FIELD_NAME.PLACE_USER_RELATIONS,
          this.$t('thePlaceEditor.field.placeUserRelations'),
        ],
        [
          FIELD_NAME.PLACE_ATTACH_FILES,
          this.$t('thePlaceEditor.field.placeAttachFiles'),
        ],
        ...this.attributes.map((attr) => [`${attr.id}`, attr.attribute_name]),
      ])
    },
    /**
     * プレイスグループのselectValues
     * @type {Array.<Object>}
     */
    placeGroupSelectValues: function () {
      if (!this.groups) {
        return []
      }

      return this.groups.map((group) => {
        return {
          text: group.place_group_name,
          value: group.id,
        }
      })
    },
    /**
     * プレイス属性のID文字列配列
     * @type {Array.<String>}
     */
    placeAttributeIdStrings: function () {
      if (!this.attributes) {
        return []
      }
      return this.attributes.map((attr) => `${attr.id}`)
    },
    /**
     * フォーム用データ(定型分)
     * @type {Array.<Object>}
     */
    formsDefault: function () {
      const form = [
        {
          // プレイス名
          fieldName: FIELD_NAME.PLACE_NAME,
          label: this.fieldLabelMap.get(FIELD_NAME.PLACE_NAME),
          attributeType: ATTRIBUTE_TYPE.STRING,
          value: this.isCreate ? null : this.data.place_name,
          validateRule: {
            required: true,
            max: 128,
          },
          placeholder: this.$t('thePlaceEditor.example.placeName'),
        },
        {
          // フリガナ
          fieldName: FIELD_NAME.PLACE_NAME_KANA,
          label: this.fieldLabelMap.get(FIELD_NAME.PLACE_NAME_KANA),
          attributeType: ATTRIBUTE_TYPE.STRING,
          value: this.isCreate ? null : this.data.place_name_kana,
          validateRule: {
            max: 256,
          },
          placeholder: this.$t('thePlaceEditor.example.placeNameKana'),
        },
        {
          // プレイスコード
          fieldName: FIELD_NAME.PLACE_CODE,
          label: this.fieldLabelMap.get(FIELD_NAME.PLACE_CODE),
          attributeType: ATTRIBUTE_TYPE.STRING,
          value: this.isCreate ? null : this.data.place_code,
          validateRule: {
            max: 50,
          },
          placeholder: this.$t('thePlaceEditor.example.placeCode'),
        },
        {
          // 郵便番号
          fieldName: FIELD_NAME.POSTAL_CODE,
          label: this.fieldLabelMap.get(FIELD_NAME.POSTAL_CODE),
          attributeType: ATTRIBUTE_TYPE.STRING,
          value: this.isCreate ? null : this.data.postal_code,
          validateRule: {
            regex: /^[0-9]{3}(-)?[0-9]{2,4}$/,
            numeric_dash: true,
            max: 8,
          },
          placeholder: this.$t('thePlaceEditor.example.postalCode'),
        },
        {
          // 住所1
          fieldName: FIELD_NAME.ADDRESS1,
          label: this.fieldLabelMap.get(FIELD_NAME.ADDRESS1),
          attributeType: ATTRIBUTE_TYPE.STRING,
          value: this.isCreate ? null : this.data.address1,
          validateRule: {
            max: 128,
          },
          placeholder: this.$t('thePlaceEditor.example.address1'),
        },
        {
          // 住所2
          fieldName: FIELD_NAME.ADDRESS2,
          label: this.fieldLabelMap.get(FIELD_NAME.ADDRESS2),
          attributeType: ATTRIBUTE_TYPE.STRING,
          value: this.isCreate ? null : this.data.address2,
          validateRule: {
            max: 128,
          },
          placeholder: this.$t('thePlaceEditor.example.address2'),
        },
        {
          // 電話番号
          fieldName: FIELD_NAME.TEL,
          label: this.fieldLabelMap.get(FIELD_NAME.TEL),
          attributeType: ATTRIBUTE_TYPE.STRING,
          value: this.isCreate ? null : this.data.tel,
          validateRule: {
            regex: /^(0[0-9]{1,3}(-)?)?[0-9]{2,4}(-)?[0-9]{4}$/,
            numeric_dash: true,
            max: 15,
          },
          placeholder: this.$t('thePlaceEditor.example.tel'),
        },
        {
          // FAX番号
          fieldName: FIELD_NAME.FAX,
          label: this.fieldLabelMap.get(FIELD_NAME.FAX),
          attributeType: ATTRIBUTE_TYPE.STRING,
          value: this.isCreate ? null : this.data.fax,
          validateRule: {
            regex: /^(0[0-9]{1,3}(-)?)?[0-9]{2,4}(-)?[0-9]{4}$/,
            numeric_dash: true,
            max: 15,
          },
          placeholder: this.$t('thePlaceEditor.example.fax'),
        },
        {
          // メモ
          fieldName: FIELD_NAME.REMARKS,
          label: this.fieldLabelMap.get(FIELD_NAME.REMARKS),
          attributeType: ATTRIBUTE_TYPE.TEXT,
          value: this.isCreate ? null : this.data.remarks,
          placeholder: this.$t('thePlaceEditor.placeholder.remarks'),
        },
        // 現場
        {
          fieldName: FIELD_NAME.INDOOR_SITE,
          label: this.$t('thePlaceEditor.field.indoorSite'), // APIエラー表示に使わないので直接指定
          attributeType: ATTRIBUTE_TYPE.SLOT,
          value: null,
          indoor: true, // フィルタ用識別子
        },
        // 建物
        {
          fieldName: FIELD_NAME.INDOOR_BUILDING,
          label: this.$t('thePlaceEditor.field.indoorBuilding'), // APIエラー表示に使わないので直接指定
          attributeType: ATTRIBUTE_TYPE.SLOT,
          value: null,
          indoor: true, // フィルタ用識別子
        },
        // 屋内レベル
        {
          fieldName: FIELD_NAME.INDOOR_LEVEL,
          label: this.fieldLabelMap.get(FIELD_NAME.INDOOR_LEVEL),
          attributeType: ATTRIBUTE_TYPE.SLOT,
          value: this.levelId,
          indoor: true, // フィルタ用識別子
        },
        {
          // プレイス位置
          fieldName: FIELD_NAME.GEOMETRY,
          label: this.fieldLabelMap.get(FIELD_NAME.GEOMETRY),
          attributeType: ATTRIBUTE_TYPE.GEOMETRY,
          value: this.isCreate ? null : this.data.geom.geometry,
          disabled: !this.isCreate && this.data.position_fix_flg,
          validateRule: {
            required: this.isCreate || !this.data.position_fix_flg,
          },
          geocodeTargetName: FIELD_NAME.ADDRESS1,
          modalSize: 'xl',
        },
        {
          // グループ
          fieldName: FIELD_NAME.PLACE_GROUPS,
          label: this.fieldLabelMap.get(FIELD_NAME.PLACE_GROUPS),
          attributeType: ATTRIBUTE_TYPE.SINGLE_SELECT,
          value: this.isCreate
            ? null
            : this.data.place_groups[0] &&
              this.data.place_groups[0].place_group_id,
          selectValues: this.placeGroupSelectValues,
          validateRule: {
            required: true,
          },
        },
        {
          // 担当者
          fieldName: FIELD_NAME.PLACE_USER_RELATIONS,
          label: this.fieldLabelMap.get(FIELD_NAME.PLACE_USER_RELATIONS),
          attributeType: ATTRIBUTE_TYPE.MULTI_USER,
          value: this.isCreate
            ? null
            : {
                user: (this.data.place_user_relations || []).map(
                  (user) => user.user_id
                ),
                organization: (
                  this.data.place_organization_relations || []
                ).map((organization) => organization.organization_id),
              },
          keywordTarget: this.keywordTargets,
          allOrganizations: true,
          primaryOrganizationId:
            this.$store.getters['userProfile/defaultOrganizationId'],
          indoorSites: this.$store.getters['indoorSite/indoorSites'],
          onKeywordTargetChange: (data) =>
            this.$store.dispatch('userKeywordTarget/setKeywordTargets', data),
        },
      ]

      return this.isIndoorAvailable ? form : form.filter((f) => !f.indoor)
    },
    /**
     * フォーム用データ(プレイス属性分)
     * @type {Array.<Object>}
     */
    formsAttribute: function () {
      if (!this.attributes || this.attributes.length === 0) {
        return []
      }

      const sortedAttributes = _.sortBy(this.attributes, [
        (item) => item.sort_order,
      ])
      return sortedAttributes.map((attr) => {
        const field = {
          fieldName: `${attr.id}`,
          label: attr.attribute_name,
          attributeType:
            // 文字列属性の複数行表示対応のため attribute_type を読み替える
            // DBのcode_settingの値変更は影響範囲が広く不可なので、フロント側で読み替えている
            attr.attribute_type == ATTRIBUTE_TYPE.STRING
              ? ATTRIBUTE_TYPE.TEXT
              : +attr.attribute_type,
          value: this.$_getAttributeValueFromData(attr.id),
        }

        let selectValues = null
        if (+attr.attribute_type === ATTRIBUTE_TYPE.SINGLE_SELECT) {
          if (attr.select_value) {
            selectValues = attr.select_value
              .split(ATTRIBUTE_VALUES_SEPARATOR)
              .map((v) => ({ value: v, text: v }))
          }
          field.selectValues = [{ value: null, text: '' }].concat(
            selectValues || []
          )
        } else if (+attr.attribute_type === ATTRIBUTE_TYPE.CHECK) {
          if (attr.select_value) {
            field.selectValues = attr.select_value
              .split(ATTRIBUTE_VALUES_SEPARATOR)
              .map((v) => ({ value: v, text: v }))
          }
        } else if (+attr.attribute_type === ATTRIBUTE_TYPE.TOGGLE) {
          if (attr.select_value) {
            const labels = attr.select_value.split(ATTRIBUTE_VALUES_SEPARATOR)
            field.selectValues = [
              { value: true, text: labels[0] },
              { value: false, text: labels[1] },
            ]
          } else {
            // トグルの場合selectValues未指定はフォームが表示されてしまうので属性値の場合はわざとエラーにする
            field.selectValues = []
          }
        }

        return field
      })
    },
    /**
     * フォーム用データ(添付ファイル分)
     * @type {Array.<Object>}
     */
    formsAttachFiles: function () {
      return [
        {
          fieldName: FIELD_NAME.PLACE_ATTACH_FILES,
          label: this.fieldLabelMap.get(FIELD_NAME.PLACE_ATTACH_FILES),
          attributeType: ATTRIBUTE_TYPE.FILE,
          value: this.isCreate
            ? null
            : { attachFiles: this.data.place_attach_files },
          multiple: true,
          accept:
            'image/png,image/jpeg,image/gif,image/tiff,application/pdf,video/mp4',
          acceptLabel: 'png, jpg, gif, tiff, pdf, mp4',
          recordId: this.isCreate ? null : this.data.id,
        },
      ]
    },
    /**
     * フォーム用データ(結合)
     * @type {Array.<Object>}
     */
    forms: function () {
      return [
        ...this.formsDefault,
        ...this.formsAttribute,
        ...this.formsAttachFiles,
      ]
    },
    /**
     * 建物選択肢のID
     * @type {Array.<Number>}
     */
    buildingOptionIds: function () {
      if (this.buildingId) {
        return [this.buildingId]
      }
      return (
        (this.buildingOptions && this.buildingOptions.map((item) => item.id)) ||
        null
      )
    },
    /**
     * 屋内レベルデータ
     * @type {Object}
     */
    level: function () {
      return this.indoorLevels.find((l) => l.id === this.levelId) || null
    },
    /**
     * 建物データ
     * @type {Object}
     */
    building: function () {
      return this.indoorBuildings.find((b) => b.id === this.buildingId) || null
    },
    /**
     * 屋内レベルbounds
     * @type {Object}
     */
    indoorLevelBounds: function () {
      if (!this.level || !this.level.geom) return null
      const { north, south, east, west } = this.level.geom
      return {
        sw: [west, south],
        ne: [east, north],
      }
    },
    /**
     * 現在の屋内レベルに対応する建物
     * @type {Object}
     */
    buildingOfLevel: function () {
      if (
        !this.level ||
        !this.level.indoor_buildings ||
        !this.level.indoor_buildings.length
      ) {
        return null
      }
      const buildingId = this.level.indoor_buildings[0].id
      return this.indoorBuildings.find((b) => b.id === buildingId) || null
    },
  },
  mounted: function () {
    this.$log.debug('place editor mounted!')
  },
  methods: {
    /**
     * プレイス編集コントロールを作成
     * @param {number} id プレイスID
     */
    showModal: async function (id) {
      this.$_showInitialLoading()
      // 新規作成フラグを設定
      this.isCreate = !id
      // プレイスグループ,プレイスユーザー属性取得,マップレイヤー更新
      const promises = [
        this.$_getPlaceGroups(),
        this.$_getPlaceAttributes(),
        this.$_updateMapLayers(),
      ]
      // 編集ならプレイス情報を取得
      if (!this.isCreate) {
        promises.push(this.$_getPlace(id))
      }
      try {
        await Promise.all(promises)
      } catch {
        this.$_hideInitialLoading()
        return
      }
      this.$_initializeIndoorState()

      this.$bvModal.show(this.modalId)
      await this.$nextTick().then(() => {
        this.$_setData()
      })
      this.$_hideInitialLoading()
    },

    /**
     * 屋内用のデータ初期化
     */
    $_initializeIndoorState: function () {
      // 表示前状態保存
      this.originalIndoorBuildings = [...this.activeIndoorBuildings]
      this.originalActiveIndoorLevelId = this.activeIndoorLevelId
      // 初期屋内レベル設定
      this.levelId = this.isCreate ? null : this.data.indoor_level_id
    },

    /**
     * モーダル表示完了時処理
     */
    $_onShownModal: function () {
      // 背後の地図描画に反映させないためにモーダル表示完了してから地図側に屋内レベルを同期する
      this.$nextTick().then(() => {
        this.$_setLevelId(this.levelId)
      })
    },

    /**
     * プレイスグループ情報を取得する
     */
    $_getPlaceGroups: async function () {
      let url = RestApiClient.createApiUrl('place_group')
      try {
        const json = await RestApiClient.executeGetApi(url)
        this.groups = json.data
      } catch (error) {
        this.handleApiError(error)
        throw error
      }
    },

    /**
     * プレイスユーザー属性情報を取得する
     */
    $_getPlaceAttributes: async function () {
      let url = RestApiClient.createApiUrl('place_attribute')
      try {
        const json = await RestApiClient.executeGetApi(url)
        this.attributes = json.data
      } catch (error) {
        this.handleApiError(error)
        throw error
      }
    },

    /**
     * マップレイヤー更新
     */
    $_updateMapLayers: async function () {
      try {
        await this.$store.dispatch('mapLayer/updateMapLayersWithThrottle')
      } catch (error) {
        this.handleApiError(error, () =>
          this.$log.warn('failed to fetch maplayers. use fetched before.')
        )
      }
    },

    /**
     * プレイス情報を取得する
     * @param {number} id プレイスID
     * @return {Promise}
     */
    $_getPlace: async function (id) {
      let url = RestApiClient.createApiUrl(`place/${id}`)
      try {
        const d = await RestApiClient.executeGetApi(url).then(
          ({ data }) => data
        )
        this.data = {
          ...d,
          place_attributes: d.place_attributes.map((attr) =>
            toLocalTzAttribute(attr)
          ),
        }
      } catch (error) {
        this.handleApiError(error)
        throw error
      }
    },

    /**
     * データをフォームにセットする
     */
    $_setData: function () {
      const form = this.$refs['form-group']
      form && form.setData(this.forms)
    },

    /**
     * dataから指定プレイス属性IDの値を取得する
     * @param {Number} id プレイス属性ID
     * @returns {*} 値
     */
    $_getAttributeValueFromData: function (id) {
      if (this.isCreate) {
        return null
      }

      const matchedAttribute = this.data.place_attributes.find(
        (attr) => attr.attribute_id === id
      )
      if (!matchedAttribute) {
        return null
      }

      return this.$_convertAttributeValueToForm(matchedAttribute)
    },

    /**
     * プレイス属性の値をフォーム用に変換する
     * @param {Object} attribute 属性データ
     * @returns {*} 値
     */
    $_convertAttributeValueToForm: function (attribute) {
      const type = attribute.attribute_type
      const value = attribute.attribute_value

      if (value == null) {
        return value
      } else if (type == ATTRIBUTE_TYPE.DATETIME) {
        return moment(value).format(DATETIME_FORMAT_FORM)
      } else if (type == ATTRIBUTE_TYPE.TOGGLE) {
        const baseAttribute = this.attributes.find(
          (attr) => attr.id === attribute.attribute_id
        )
        const selectValues = baseAttribute.select_value
          ? baseAttribute.select_value.split(ATTRIBUTE_VALUES_SEPARATOR)
          : []
        return !selectValues.indexOf(value)
      } else if (value.indexOf(ATTRIBUTE_VALUES_SEPARATOR) > 0) {
        return value.split(ATTRIBUTE_VALUES_SEPARATOR)
      } else {
        return value
      }
    },

    /**
     * マップ初期化時処理
     */
    $_onMapInitEnd: function (editStart) {
      editStart()
      this.$nextTick().then(() => {
        // 屋内レベル選択済みのときはレベルの範囲にfitする(既に紐付いているプレイスの編集場面を想定)
        if (this.indoorLevelBounds) {
          this.$refs['map-control'].fitBounds(this.indoorLevelBounds)
        }
      })
    },

    /**
     * 現場IDを設定する
     * @param {Number} id 現場ID
     * @param {Function} setValue フォームへの値設定関数
     */
    $_setSiteId: function (id, setValue = NO_OP) {
      this.siteId = id
      setValue(id)
    },

    /**
     * 建物IDを設定する
     * @param {Number} id 建物ID
     * @param {Function} setValue フォームへの値設定関数
     */
    $_setBuildingId: function (id, setValue = NO_OP) {
      this.buildingId = id
      setValue(id)

      // 建物選択解除時は地図側を変更しない
      if (!id) return
      // 地図側の建物を同期
      if (this.buildingId !== this.activeIndoorBuildingId) {
        this.changeIndoorBuilding(this.building)
      }
      // 地図側の屋内レベルをフォームに反映
      if (this.levelId !== this.activeIndoorLevelId) {
        this.$refs['form'].setValue(
          FIELD_NAME.INDOOR_LEVEL,
          this.activeIndoorLevelId
        )
      }
    },

    /**
     * 建物の選択値を設定する
     * @param {Array.<Object>} buildings 建物
     */
    $_setBuildingOptions: function (buildings) {
      this.buildingOptions = buildings
    },

    /**
     * 屋内レベルIDを設定する
     * @param {Number} id 屋内レベルID
     * @param {Function} setValue フォームへの値設定関数
     */
    $_setLevelId: function (id, setValue = NO_OP) {
      this.levelId = id
      setValue(id)

      // 地図側の屋内レベルを同期
      if (this.levelId !== this.activeIndoorLevelId) {
        this.setActiveIndoorLevelId(this.levelId)
      }
      // 建物が選択されていなかったら先頭の建物を地図側に反映
      if (!this.buildingId && this.buildingOfLevel) {
        this.changeIndoorBuilding(this.buildingOfLevel)
      }
    },

    /**
     * フレイスを保存する
     */
    $_savePlace: async function () {
      // フォーム値を取得
      let values
      try {
        values = this.$refs['form-group'].getValues()
      } catch (e) {
        this.$log.error(e)
        return
      }

      this.$_showSaveLoading()
      const requestBody = this.$_convertValuesToRequestBody(values)

      let id
      try {
        if (this.isCreate) {
          id = await this.$_createPlace(requestBody)
        } else {
          await this.$_updatePlace(requestBody)
          id = this.data.id
        }
      } catch {
        this.$_hideSaveLoading()
        return
      }

      this.$log.debug(
        `place ${this.isCreate ? 'create' : 'edit'} success: id ${id}`
      )
      this.$_emitEditEnd(id)
      this.$_hideSaveLoading()
      this.$_showSuccess()
      this.$bvModal.hide(this.modalId)
    },

    /**
     * フォームから取得した値をリクエストのbodyに変換する
     * @param {Array.<Object>} values フォームの値
     * @returns {Object} リクエストボディ
     */
    $_convertValuesToRequestBody: function (values) {
      const valueGroup = _.groupBy(values, (item) =>
        this.placeAttributeIdStrings.includes(item.name)
          ? 'attribute'
          : 'default'
      )

      const body = {}
      // 定型分
      this.$_applyDefaultValuesToRequestBody(body, valueGroup.default)
      // プレイス属性分
      this.$_applyAttributeValuesToRequestBody(body, valueGroup.attribute)
      // 位置確定フラグ
      body.position_fix_flg = this.isCreate ? false : this.data.position_fix_flg
      // 自動チェックイン範囲
      body.checkin_radius = this.isCreate ? null : this.data.checkin_radius

      return body
    },

    /**
     * 定型分の入力値をリクエストのbodyに反映する
     * @param {Object} body リクエストボディ
     * @param {Array.<Object>} values フォーム入力値
     */
    $_applyDefaultValuesToRequestBody: function (body, values) {
      values.forEach((item) => {
        if (item.name === FIELD_NAME.PLACE_USER_RELATIONS) {
          body.place_user_relations = item.value
            ? item.value.user.map((id) => ({ user_id: id }))
            : []
          body.place_organization_relations = item.value
            ? item.value.organization.map((id) => ({ organization_id: id }))
            : []
        } else if (item.value === null) {
          body[item.name] = item.value
        } else if (item.name === FIELD_NAME.PLACE_GROUPS) {
          body.place_groups = [{ place_group_id: item.value }]
        } else if (item.name === FIELD_NAME.PLACE_ATTACH_FILES) {
          const { attachFiles, tempFiles } = item.value || {}
          if (attachFiles && attachFiles.length) {
            body.place_attach_files = attachFiles.map((item) => ({
              id: item.id,
            }))
          }
          if (tempFiles && tempFiles.length) {
            body.temp_attach_files = tempFiles.map((item) => ({
              temp_file_path: item.tempFilePath,
            }))
          }
        } else {
          body[item.name] = item.value
        }
      })
    },

    /**
     * プレイス属性分の入力値をリクエストのbodyに反映する
     * @param {Object} body リクエストボディ
     * @param {Array.<Object>} values フォーム入力値
     */
    $_applyAttributeValuesToRequestBody: function (body, values) {
      if (!Array.isArray(values) || values.length === 0) {
        return
      }

      const attributes = values.map((item) => {
        const baseAttribute = this.attributes.find(
          (attr) => attr.id === +item.name
        )
        const type = baseAttribute.attribute_type

        // トグル属性がnullのときfalseを代入
        if (type == ATTRIBUTE_TYPE.TOGGLE && item.value == null) {
          item.value = false
        }

        let value
        if (item.value === null) {
          value = item.value
        } else if (type == ATTRIBUTE_TYPE.DATETIME) {
          value = toServerTz(item.value)
        } else if (type == ATTRIBUTE_TYPE.TOGGLE) {
          const selectValues = baseAttribute.select_value.split(
            ATTRIBUTE_VALUES_SEPARATOR
          )
          value = selectValues[+!item.value]
        } else if (Array.isArray(item.value)) {
          value = item.value.join(ATTRIBUTE_VALUES_SEPARATOR)
        } else {
          value = item.value
        }

        return {
          attribute_id: +item.name,
          attribute_value: value,
        }
      })

      body.place_attributes = attributes
    },

    /**
     * プレイスを作成する
     *
     * @param {Object} requestBody リクエストボディ
     * @returns {Promise}
     */
    $_createPlace: async function (requestBody) {
      const url = RestApiClient.createApiUrl('place')

      try {
        return await RestApiClient.executePostApi(url, requestBody)
      } catch (e) {
        this.handleApiError(e, this.$_showError)
        throw e
      }
    },

    /**
     * プレイスを更新する
     *
     * @param {Object} requestBody リクエストボディ
     * @returns {Promise}
     */
    $_updatePlace: async function (requestBody) {
      const url = RestApiClient.createApiUrl(`place/${this.data.id}`)

      try {
        return await RestApiClient.executePutApi(url, requestBody)
      } catch (e) {
        this.handleApiError(e, this.$_showError)
        throw e
      }
    },

    /**
     * 成功アラートを出す
     */
    $_showSuccess: function () {
      this.$toast.fire({
        position: 'top',
        icon: 'success',
        title: this.messages.successTitle,
        showConfirmButton: true,
        confirmButtonColor: '#6c757d',
        timer: 2000,
      })
    },

    /**
     * エラーメッセージを出す
     * @param {Error|Response|String} error エラー
     */
    $_showError: async function (error) {
      if (!(error instanceof Response)) {
        this.showApiError(error)
        return
      }

      const json = await error.json()
      let errorMessages = []
      if (json.errors) {
        errorMessages = json.errors.map((error) => {
          const label = this.fieldLabelMap.get(error.field)
          if (label) {
            return this.$t('thePlaceEditor.message.invalidName', {
              name: label,
            })
          } else {
            return error.message
          }
        })
      } else {
        errorMessages = [json.message]
      }

      this.$alert.fire({
        icon: 'error',
        title: this.messages.errorTitle,
        html: errorMessages.join('<br/>'),
      })
    },

    /**
     * 編集終了イベントを発行する
     * @param {Number} id 更新されたID
     */
    $_emitEditEnd: function (id) {
      /**
       * プレイス編集完了イベント
       * @param {Number} id 更新されたID
       */
      this.$emit('place-editend', id)
    },

    /**
     * モーダル非表示時処理
     */
    $_onHideModal: function () {
      this.$_restoreMapIndoorState()
      this.$_clearForms()
    },

    /**
     * 地図側屋内状態を復元する
     */
    $_restoreMapIndoorState: function () {
      this.setActiveIndoorLevelId(this.originalActiveIndoorLevelId)
      this.changeIndoorBuilding(this.originalIndoorBuildings[0])
    },

    /**
     * フォーム情報をクリアする
     */
    $_clearForms: function () {
      this.data = null
      this.levelId = null
      this.buildingId = null
      this.buildingOptions = null
      this.siteId = null
      this.originalIndoorBuildings = []
      this.originalActiveIndoorLevelId = null
      this.isCreate = true
    },

    /**
     * 初期表示時のローディングを表示する
     */
    $_showInitialLoading: function () {
      this.isLoading = true
    },

    /**
     * 初期表示時のローディングを非表示にする
     */
    $_hideInitialLoading: function () {
      this.isLoading = false
    },

    /**
     * 保存時のローディングを表示する
     */
    $_showSaveLoading: function () {
      this.isSaving = true
    },

    /**
     * 保存時のローディングを非表示にする
     */
    $_hideSaveLoading: function () {
      this.isSaving = false
    },
  },
}
</script>

<style lang="scss" scoped>
// 屋内ビル解除ボタンの無効化
// Slotの実装が煩雑化になる可能性があるため
// 例外的にスタイルで解除ボタンを無効化
#place-editor-map-control {
  ::v-deep .selected-building {
    min-width: 0;
    & > .button-cancel {
      display: none;
    }
  }
}
</style>
